// QuickTemplatesMenu.js
import React, { useCallback } from 'react';
import {
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    IconButton,
    Button,
    Box,
    Text,
    HStack,
} from '@chakra-ui/react';
import {
    MdOutlineDescription,
    MdOutlinePlaylistAddCheck,
    MdOutlineSummarize,
    MdCode,
} from 'react-icons/md';

const templates = [
    {
        category: 'Featured',
        items: [
            {
                label: 'Code Wrapper',
                icon: MdCode,
                template: '```\n{CURSOR_OR_SELECTION}\n```\n\n',
                description: 'Triple backticks for code',
            },
            {
                label: 'Context Wrapper',
                icon: MdOutlineDescription,
                template: '# context\n\n```\n{CURSOR_OR_SELECTION}\n```\n\n# task\n\n',
                description: 'Structure for context + specific task',
            },
            {
                label: 'Handoff context',
                icon: MdOutlineSummarize,
                template: `You are preparing a handoff to continue this work in a fresh conversation (the current one is too long). Produce a self-contained summary with two parts.

## PART 1 — Files to reload

List the minimal set of files needed to resume this task, as a ready-to-run 
command. Include only files that are actively relevant to the remaining work — 
not everything we touched. Use glob patterns where sensible.

Format:
\`\`\`bash
clear && print_context <files>
\`\`\`

## PART 2 — Handoff brief

Write a brief the next assistant can act on immediately:

**GOAL** — What we're ultimately trying to achieve.

**CURRENT STATE** — What works now, what's been built/changed this session, 
and anything left in a broken or half-finished state.

**REMAINING WORK** — Concrete next steps, in priority order.

**KEY CONTEXT** — Decisions made and their rationale, constraints, gotchas, 
dead-ends already ruled out, and any naming/structure conventions that 
aren't obvious from the code.

Be specific (file names, function names, error messages). Omit anything the 
next assistant can infer from reading the reloaded files. Do not pad.`,
                description: 'Prepare context for new conversation',
            },
            {
                label: 'Review Writing',
                icon: MdOutlinePlaylistAddCheck,
                template: '# passage to review\n\n{CURSOR_OR_SELECTION}\n\n# task\n\n',
                description: 'Review specific passage',
            },
        ],
    },
];

const QuickTemplatesMenu = ({
    onTriggerMouseDown,
    getSelection,
    getActiveTextarea,
    // Uncontrolled API: getValue reads DOM, setValue writes to DOM
    getValue,
    setValue,
    size = 'md',
    variant = 'solid',
    colorScheme = 'gray',
    displayMode = 'icon',
}) => {
    const handleInsert = useCallback((rawTemplate) => {
        // Read value from DOM at the moment of the click — never stale
        const currentValue = getValue();

        const sel = (typeof getSelection === 'function' ? getSelection() : null)
            || { start: currentValue.length, end: currentValue.length, text: '' };

        let processed = rawTemplate.replace(/\{CURSOR_OR_SELECTION\}/g, sel.text);

        // {CURSOR} marks where to place the caret after insertion
        const cursorIndex = processed.indexOf('{CURSOR}');
        if (cursorIndex !== -1) {
            processed = processed.replace('{CURSOR}', '');
        }

        const newValue =
            currentValue.slice(0, sel.start) +
            processed +
            currentValue.slice(sel.end);

        const newCaretPos = cursorIndex !== -1
            ? sel.start + cursorIndex
            : sel.start + processed.length;

        // Write value and caret in one call, then focus
        setValue(newValue, newCaretPos);

        requestAnimationFrame(() => {
            const ta = typeof getActiveTextarea === 'function'
                ? getActiveTextarea()
                : null;
            if (ta) ta.focus();
        });
    }, [getValue, setValue, getSelection, getActiveTextarea]);

    return (
        <Menu closeOnSelect>
            <MenuButton
                as={displayMode === 'icon' ? IconButton : Button}
                icon={displayMode === 'icon' ? <MdCode size="20px" /> : undefined}
                leftIcon={displayMode === 'text' ? <MdCode /> : undefined}
                variant={variant}
                colorScheme={colorScheme}
                size={size}
                isRound={displayMode === 'icon'}
                minW={displayMode === 'icon' ? '48px' : undefined}
                h="48px"
                aria-label="Templates"
                title="Message templates"
                onMouseDown={(e) => {
                    // Prevent focus shift so the selection in the textarea is preserved
                    e.preventDefault();
                    if (typeof onTriggerMouseDown === 'function') {
                        onTriggerMouseDown();
                    }
                }}
            >
                {displayMode === 'text' && 'Templates'}
            </MenuButton>

            <MenuList
                maxW="380px"
                maxH="450px"
                overflowY="auto"
                border="1px"
                borderColor="gray.200"
                shadow="lg"
                zIndex={1000}
            >
                {templates.map((section, idx) => (
                    <Box key={section.category}>
                        {idx > 0 && <MenuDivider />}
                        <MenuItem
                            isDisabled
                            fontSize="xs"
                            fontWeight="600"
                            color="gray.600"
                            bg="gray.50"
                            _hover={{ bg: 'gray.50' }}
                        >
                            {section.category.toUpperCase()}
                        </MenuItem>

                        {section.items.map(item => {
                            const IconComponent = item.icon;
                            return (
                                <MenuItem
                                    key={item.label}
                                    onClick={() => handleInsert(item.template)}
                                    py={3}
                                    _hover={{ bg: 'gray.50' }}
                                    fontSize="sm"
                                >
                                    <HStack spacing={3} w="full" align="flex-start">
                                        <IconComponent
                                            size="18px"
                                            color="gray.600"
                                            style={{ marginTop: '2px', flexShrink: 0 }}
                                        />
                                        <Box flex="1" minW="0">
                                            <Text fontWeight="500" mb={1}>{item.label}</Text>
                                            {item.description && (
                                                <Text fontSize="xs" color="gray.500" lineHeight="1.3">
                                                    {item.description}
                                                </Text>
                                            )}
                                        </Box>
                                    </HStack>
                                </MenuItem>
                            );
                        })}
                    </Box>
                ))}
            </MenuList>
        </Menu>
    );
};

export default QuickTemplatesMenu;